import React from 'react';
import { useTranslation } from 'react-i18next';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import {
  Input,
  Stack,
  Button,
  styled,
  List,
  ListItem,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ColorsV2 } from 'app/constants/color';
import { Buttons, WhiteSpace } from 'app/constants/sizing';
import apiService from 'app/services/apiService';

function FileUpload({
  selectedFiles,
  setSelectedFiles,
  previouslyAddedFiles,
  setPreviouslyAddedFiles,
  setDeletedFiles,
}) {
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes
  const MAX_FILES = 3;
  const MIME_TYPES = {
    'aac': 'audio/aac',
    'abw': 'application/x-abiword',
    'arc': 'application/x-freearc',
    'avi': 'video/x-msvideo',
    'azw': 'application/vnd.amazon.ebook',
    'bin': 'application/octet-stream',
    'bmp': 'image/bmp',
    'bz': 'application/x-bzip',
    'bz2': 'application/x-bzip2',
    'csh': 'application/x-csh',
    'css': 'text/css',
    'csv': 'text/csv',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'eot': 'application/vnd.ms-fontobject',
    'epub': 'application/epub+zip',
    'gz': 'application/gzip',
    'gif': 'image/gif',
    'htm': 'text/html',
    'html': 'text/html',
    'ico': 'image/vnd.microsoft.icon',
    'ics': 'text/calendar',
    'jar': 'application/java-archive',
    'jpeg': 'image/jpeg',
    'jpg': 'image/jpeg',
    'js': 'text/javascript',
    'json': 'application/json',
    'jsonld': 'application/ld+json',
    'mid': 'audio/midi',
    'midi': 'audio/midi',
    'mjs': 'text/javascript',
    'mp3': 'audio/mpeg',
    'mpeg': 'video/mpeg',
    'mpkg': 'application/vnd.apple.installer+xml',
    'odp': 'application/vnd.oasis.opendocument.presentation',
    'ods': 'application/vnd.oasis.opendocument.spreadsheet',
    'odt': 'application/vnd.oasis.opendocument.text',
    'oga': 'audio/ogg',
    'ogv': 'video/ogg',
    'ogx': 'application/ogg',
    'opus': 'audio/opus',
    'otf': 'font/otf',
    'png': 'image/png',
    'pdf': 'application/pdf',
    'php': 'application/x-httpd-php',
    'ppt': 'application/vnd.ms-powerpoint',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'rar': 'application/vnd.rar',
    'rtf': 'application/rtf',
    'sh': 'application/x-sh',
    'svg': 'image/svg+xml',
    'swf': 'application/x-shockwave-flash',
    'tar': 'application/x-tar',
    'tif': 'image/tiff',
    'tiff': 'image/tiff',
    'ts': 'video/mp2t',
    'ttf': 'font/ttf',
    'txt': 'text/plain',
    'vsd': 'application/vnd.visio',
    'wav': 'audio/wav',
    'weba': 'audio/webm',
    'webm': 'video/webm',
    'webp': 'image/webp',
    'woff': 'font/woff',
    'woff2': 'font/woff2',
    'xhtml': 'application/xhtml+xml',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'xml': 'application/xml',
    'xul': 'application/vnd.mozilla.xul+xml',
    'zip': 'application/zip',
    '3gp': 'video/3gpp',
    '3g2': 'video/3gpp2',
    '7z': 'application/x-7z-compressed',
  };

  const downloadFile = async ({ fileObjectId, directDownload = false }) => {
    if (!fileObjectId) return;

    try {
      const response = await apiService.downloadFile(fileObjectId);

      const fileName = response.headers['content-disposition'];
      let fileExtension = '';

      // Loop through the filename backwards until you hit . to gather the file extension
      // In the future it's preferable for the mime-type to come directly with the backend response instead
      for (let i = fileName?.length - 1 || false; i >= 0; i--) {
        if (!i) break;
        if (fileName[i] === '.') {
          fileExtension = fileExtension.split('').reverse().join(''); // Reverse the file extension string since it will have been put together backwards
          break;
        }
        fileExtension += fileName[i];
      }
      const contentType = MIME_TYPES[fileExtension] || '';

      // Create a Blob from the response data
      const blob = new Blob([response.data], { type: contentType });

      // Create an object URL for the Blob
      const url = URL.createObjectURL(blob);

      if (directDownload) {
        // Trigger a direct download
        const link = document.createElement('a');
        link.href = url;
        link.download =
          response.headers['content-disposition']?.match(/filename="(.+)"/)?.[1] ||
          'downloaded_file';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the object URL
        URL.revokeObjectURL(url);
      } else {
        // Open the file in a new tab/window
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('Error opening file:', error);
    }
  };

  const handleFileChange = event => {
    const files = Array.from(event.target.files);
    const validFiles = [];

    // Validate each file
    files.forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        alert(`File "${file.name}" exceeds the 50MB size limit.`);
      } else {
        // Add modal that allows user to enter a custom name for the file, unless this is handled by simply allowing renaming of uploaded files
        //
        file.filename = file.name;
        validFiles.push(file);
      }
    });

    if (selectedFiles.length + validFiles.length + previouslyAddedFiles.length > MAX_FILES) {
      alert(`You can upload a maximum of ${MAX_FILES} files. Extra files were discarded.`);
      return;
    }

    const newFiles = Array.from(new Set([...selectedFiles, ...validFiles])).slice(0, MAX_FILES);

    setSelectedFiles(newFiles);
  };

  const handleRemoveFile = index => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemovePreviouslyAddedFile = id => {
    // Logic for removing previously added files by id
    setPreviouslyAddedFiles(prev => prev.filter(prevFile => prevFile.fileObjectId !== id));
    setDeletedFiles(prev => [...prev, id]);
  };

  const StyledConfirm = styled(Button)({
    background: ColorsV2.secondary,
    width: Buttons.textMedium.width,
    height: Buttons.textMedium.height,
    marginTop: WhiteSpace.xs,
    marginRight: WhiteSpace.xs,
    '&:hover': {
      backgroundColor: ColorsV2.acent,
    },
    '&:disabled': {
      color: ColorsV2.textInfo,
    },
  });

  const { t } = useTranslation('translation', { keyPrefix: 'reusable_components' });

  return (
    <Stack direction='column' alignItems='flex-start'>
      <Input
        type='file'
        onChange={handleFileChange}
        inputProps={{ multiple: true }}
        style={{ display: 'none' }}
        id='file-input'
        disabled={selectedFiles?.length >= MAX_FILES}
      />
      {setSelectedFiles && selectedFiles && <Stack direction='row' alignItems='center'>
        <label htmlFor='file-input'>
          <StyledConfirm
            variant='contained'
            component='span'
            disabled={selectedFiles?.length >= MAX_FILES}
          >
            {t('select_files')}
          </StyledConfirm>
        </label>
      </Stack>}
      <List
        sx={{
          flexDirection: 'column',
        }}
      >
        {selectedFiles.map((file, index) => (
          <ListItem key={index} sx={{ color: ColorsV2.textInfo }}>
            <Typography>
              {file.filename}{' '}
              {!isNaN(file.size) ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : ''}
            </Typography>
            {setSelectedFiles && <IconButton
              aria-label='delete'
              onClick={() => handleRemoveFile(index)}
              sx={{
                ...Buttons.baseStyle.iconButton,
                marginLeft: WhiteSpace.xs,
              }}
            >
              <DeleteOutlinedIcon />
            </IconButton>}
          </ListItem>
        ))}
        {previouslyAddedFiles?.map((file, index) => (
          <ListItem key={index} sx={{ color: ColorsV2.textInfo }}>
            <Typography>
              {file.filename}{' '}
              {!isNaN(file.size) ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : ''}
            </Typography>
            <Tooltip title={t('download_file')}>
              <IconButton
                aria-label='download'
                onClick={() =>
                  downloadFile({ fileObjectId: file.fileObjectId, directDownload: true })
                }
                sx={{
                  ...Buttons.baseStyle.iconButton,
                  marginLeft: WhiteSpace.xs,
                }}
              >
                <DownloadOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('open_file')}>
              <IconButton
                aria-label='open-in-new-window'
                onClick={() =>
                  downloadFile({ fileObjectId: file.fileObjectId, directDownload: false })
                }
                sx={{
                  ...Buttons.baseStyle.iconButton,
                  marginLeft: WhiteSpace.xs,
                }}
              >
                <LaunchOutlinedIcon />
              </IconButton>
            </Tooltip>
            {setPreviouslyAddedFiles && setDeletedFiles && <Tooltip title={t('remove_file')}>
              <IconButton
                aria-label='delete'
                onClick={() => handleRemovePreviouslyAddedFile(file.fileObjectId)}
                sx={{
                  ...Buttons.baseStyle.iconButton,
                  marginLeft: WhiteSpace.xs,
                }}
              >
                <DeleteOutlinedIcon />
              </IconButton>
            </Tooltip>}
          </ListItem>
        ))}
      </List>
    </Stack>
  );
}

export default FileUpload;
